import React, { useState } from 'react';
import {
  List,
  Avatar,
  Typography,
  Button,
  Result,
  Tag,
} from '@arco-design/web-react';
import useLocale from '../../utils/useLocale';
import styles from './style/index.module.less';

export interface MessageItemData {
  id: string;
  title: string;
  subTitle?: string;
  avatar?: string;
  content: string;
  time?: string;
  status: number;
  tag?: {
    text?: string;
    color?: string;
  };
  approvalId?: string; // 审批ID，用于跳转
  notificationType?: string; // 通知类型，用于判断是否显示"已读"按钮
}

export type MessageListType = MessageItemData[];

interface MessageListProps {
  data: MessageItemData[];
  onItemClick?: (item: MessageItemData, index: number) => void;
  onMarkAsRead?: (
    item: MessageItemData,
    event: React.MouseEvent<HTMLElement>
  ) => void;
  onSeeMoreClick?: () => void;
}

function MessageList(props: MessageListProps) {
  const t = useLocale();
  const { data, onItemClick, onMarkAsRead, onSeeMoreClick } = props;
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  function handleItemClick(item: MessageItemData, index: number) {
    onItemClick && onItemClick(item, index);
  }

  function handleMarkAsRead(
    item: MessageItemData,
    event: React.MouseEvent<HTMLElement>
  ) {
    onMarkAsRead && onMarkAsRead(item, event);
  }

  function handleSeeMoreClick() {
    onSeeMoreClick && onSeeMoreClick();
  }

  // 判断是否显示"已读"按钮：只有非 pending 类型才显示
  function shouldShowMarkAsReadButton(item: MessageItemData): boolean {
    return item.notificationType !== 'pending';
  }

  return (
    <List
      noDataElement={<Result status="404" subTitle={t['message.empty.tips']} />}
      footer={
        <div className={styles.footer}>
          <div className={styles['footer-item']} style={{ textAlign: 'center', width: '100%' }}>
            <Button type="text" size="small" onClick={handleSeeMoreClick}>
              {t['message.seeMore']}
            </Button>
          </div>
        </div>
      }
    >
      {data.map((item, index) => (
        <List.Item
          key={item.id}
          actionLayout="vertical"
          style={{
            opacity: item.status ? 0.65 : 1,
            cursor: 'pointer',
            position: 'relative',
          }}
          onClick={() => {
            handleItemClick(item, index);
          }}
          onMouseEnter={() => setHoveredItemId(item.id)}
          onMouseLeave={() => setHoveredItemId(null)}
        >
          <List.Item.Meta
            avatar={
              item.avatar && (
                <Avatar shape="circle" size={36}>
                  <img src={item.avatar} />
                </Avatar>
              )
            }
            title={
              <div className={styles['message-title']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span>{item.title}</span>
                  {item.subTitle && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {item.subTitle}
                    </Typography.Text>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {item.tag && item.tag.text ? (
                    <Tag color={item.tag.color} size="small">{item.tag.text}</Tag>
                  ) : null}
                  {/* 已读按钮：Hover时显示，只对非pending通知显示 */}
                  {shouldShowMarkAsReadButton(item) && hoveredItemId === item.id && (
                    <Button
                      type="text"
                      size="mini"
                      onClick={(event) =>
                        handleMarkAsRead(
                          item,
                          event as unknown as React.MouseEvent<HTMLElement>
                        )
                      }
                      style={{ padding: '0 4px', fontSize: 12 }}
                    >
                      已读
                    </Button>
                  )}
                </div>
              </div>
            }
            description={
              <div>
                <Typography.Paragraph style={{ marginBottom: 0 }} ellipsis>
                  {item.content}
                </Typography.Paragraph>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {item.time}
                </Typography.Text>
              </div>
            }
          />
        </List.Item>
      ))}
    </List>
  );
}

export default MessageList;
